import "./SupportPanel.css";
import { useState, useRef, useEffect, useCallback } from "react";
import { useRestoreFocus } from "../../hooks/useFocusTrap";
import { useLearnCtx } from "./LearnPage";
import { useMathJax } from "../../hooks/useMathJax";
import {
  processText,
  svgToDataUrl,
  parseArrowKey,
} from "../../utils/learnUtils";
import MultiFigureViewer from "./content/MultiFigureViewer";
import IntFigureComp from "./content/IntFigureComp/IntFigureComp";
import IntFigureDiscreteComp from "./content/IntFigureComp/IntFigureDiscreteComp";
import SupportSearch from "./SupportSearch";
import { useSelector } from "react-redux";

/* ─── Ref-record resolver ────────────────────────────────────── */

/**
 * Returns true when the support record is a pointer into data.json
 * (figure/equation from main text) rather than a self-contained article.
 */
function isRefRecord(record) {
  return record && "section" in record && !Array.isArray(record.keywords);
}

/**
 * Resolve a ref record to the actual content item in data.json.
 * Returns null if indices are out of range.
 */
function resolveRefItem(rawData, record) {
  try {
    return rawData[record.section].subsections[record.subsection].frames[
      record.frame
    ].contents[record.content_item];
  } catch {
    return null;
  }
}

/**
 * Right-panel support viewer: renders a support.json record by ID.
 * Handles both regular support articles and ref records that point at
 * equations / figures in the main canvas data.
 *
 * @param {{ onClose: () => void, showSearch?: boolean }} props
 * @prop showSearch  When true (narrow ≤1024px mode), embeds a SupportSearch bar
 *                  at the top of the panel so the user can search without closing.
 */
export default function SupportPanel({ onClose, showSearch = false }) {
  useRestoreFocus();
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const {
    supportItemId,
    canSupportBack,
    canSupportForward,
    supportBack,
    supportForward,
    openSupport,
  } = useLearnCtx();

  // Retrieve content from reducer
  const rawData = useSelector((state) => state.ContentReducer.content);
  const rawSupport = useSelector((state) => state.SupportReducer.supportItems);

  const record = rawSupport.find((r) => r._id === supportItemId) ?? null;
  const isRef = isRefRecord(record);
  const refItem = isRef ? resolveRefItem(rawData, record) : null;

  const bodyRef = useRef(null);
  useMathJax(bodyRef, [supportItemId]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [supportItemId]);

  const handleBodyClick = useCallback(
    (e) => {
      const link = e.target.closest("[data-support-id]");
      if (!link) return;
      e.preventDefault();
      openSupport(link.dataset.supportId);
    },
    [openSupport],
  );

  /* Derive a display label for the breadcrumb */
  const label = isRef
    ? (refItem?.title ?? record?.label ?? "")
    : (record?.title ?? "");

  return (
    <div className="sp">
      <div className="sp__header">
        <div className="sp__nav">
          <button
            className="sp__nav-btn"
            onClick={supportBack}
            disabled={!canSupportBack}
            aria-label="Back"
            title="Back"
          >
            <BackIcon /> Back
          </button>
          <button
            className="sp__nav-btn"
            onClick={supportForward}
            disabled={!canSupportForward}
            aria-label="Forward"
            title="Forward"
          >
            Forward <ForwardIcon />
          </button>
        </div>
        <button
          ref={closeRef}
          className="sp__close"
          onClick={onClose}
          aria-label="Close support panel"
        >
          ✕
        </button>
      </div>

      {/* Embedded search bar — only shown in narrow (≤1024px) mode */}
      {showSearch && (
        <div className="sp__search">
          <SupportSearch />
        </div>
      )}

      <div className="sp__body" ref={bodyRef} onClick={handleBodyClick}>
        {!record ? (
          /* No item selected yet (supportItemId is null) — stay quiet in search mode */
          !supportItemId ? (
            showSearch ? null : (
              <p className="sp__empty">Support item not found.</p>
            )
          ) : (
            <p className="sp__empty">
              Support item not found. (id: {supportItemId})
            </p>
          )
        ) : isRef ? (
          refItem ? (
            <RefContent item={refItem} />
          ) : (
            <p className="sp__empty">Could not locate content.</p>
          )
        ) : (
          record.content.map((item, i) => (
            <SupportItem key={item._id ?? i} item={item} />
          ))
        )}
      </div>
    </div>
  );
}

/* ─── Ref content renderer (canvas equations / figures) ─────── */

function RefContent({ item }) {
  switch (item.type) {
    case "EDCOMP_EQUATION":
    case "EDCOMP_EQN_MULTILINE":
      return <RefEquation item={item} />;
    case "EDCOMP_FIGURE":
      return <RefFigure item={item} />;
    case "EDCOMP_MULTIFIGURE":
      return <RefMultiFigure item={item} />;
    case "EDCOMP_INT_FIGURE":
      return <RefIntFigure item={item} />;
    case "EDCOMP_INT_FIGURE_DISCRETE":
      return <RefIntFigureDiscrete item={item} />;
    default:
      return <p className="sp__empty">Unsupported content type: {item.type}</p>;
  }
}

function ensureDisplayMath(eq) {
  const trimmed = (eq ?? "")
    .replace(/\\begin\{align\}/g, "\\begin{align*}")
    .replace(/\\end\{align\}/g, "\\end{align*}")
    .replace(/\\tag\{[^}]*\}/g, "")
    .trim();
  return trimmed.startsWith("$$") && trimmed.endsWith("$$")
    ? trimmed
    : `$$${trimmed}$$`;
}

function extractTagNumber(text) {
  const m = (text ?? "").match(/\\tag\{([^}]+)\}/);
  return m ? m[1] : null;
}

function RefEquation({ item }) {
  const {
    equation,
    tooltip_elements = [],
    tooltip_descriptions = [],
    line_descriptions = [],
  } = item.data ?? {};
  const eqNumber = extractTagNumber(equation);
  return (
    <div className="sp__ref-section">
      {eqNumber && (
        <h2 className="sp__title sp__title--section">Equation {eqNumber}</h2>
      )}
      {item.title && <p className="sp__ref-subtitle">{item.title}</p>}

      <div className="sp__equation">
        <div className="sp__eq-content">{ensureDisplayMath(equation)}</div>
      </div>

      {tooltip_elements.length > 0 && (
        <section className="sp__guide-section">
          <h3 className="sp__guide-heading">Symbols</h3>
          <dl className="sp__dl">
            {tooltip_elements.map((el, i) => (
              <div key={i} className="sp__dl-row">
                <dt className="sp__dt">{`$${el}$`}</dt>
                <dd
                  className="sp__dd"
                  dangerouslySetInnerHTML={{
                    __html: tooltip_descriptions[i] ?? "",
                  }}
                />
              </div>
            ))}
          </dl>
        </section>
      )}

      {line_descriptions.length > 0 && (
        <section className="sp__guide-section">
          <h3 className="sp__guide-heading">Steps</h3>
          <ol className="sp__steps">
            {line_descriptions.map((desc, i) => (
              <li
                key={i}
                className="sp__step"
                dangerouslySetInnerHTML={{ __html: desc }}
              />
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

function RefFigure({ item }) {
  const { text: caption, image } = item.data ?? {};
  const src = image?.startsWith("data:") ? image : svgToDataUrl(image ?? "");
  const captionRef = useRef(null);
  const [activeKeys, setActiveKeys] = useState(new Set());
  const figNumber = extractTagNumber(caption);

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    const onClick = (e) => {
      const btn = e.target.closest(".ct__fig-ptr-btn");
      if (!btn) return;
      e.preventDefault();
      const key = btn.dataset.arrows;
      setActiveKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    el.querySelectorAll(".ct__fig-ptr-btn").forEach((btn) => {
      btn.classList.toggle(
        "ct__fig-ptr-btn--active",
        activeKeys.has(btn.dataset.arrows),
      );
    });
  }, [activeKeys]);

  const visibleArrows = [...activeKeys].flatMap(parseArrowKey);

  return (
    <div className="sp__ref-section">
      {figNumber && (
        <h2 className="sp__title sp__title--section">Figure {figNumber}</h2>
      )}
      {item.title && <p className="sp__ref-subtitle">{item.title}</p>}
      <div className="sp__intfig-wrap">
        <figure className="sp__figure">
          <div className="sp__figure-img-wrap">
            {image && (
              <img
                src={src}
                alt={item.title ?? "Figure"}
                className="sp__figure-img"
              />
            )}
            {visibleArrows.map(({ x, y }, i) => (
              <svg
                key={i}
                className="ct__fig-ptr-arrow"
                style={{ left: `${x}%`, top: `${y}%` }}
                width="16"
                height="28"
                viewBox="0 0 16 28"
                aria-hidden="true"
              >
                <line
                  x1="8"
                  y1="0"
                  x2="8"
                  y2="17"
                  stroke="white"
                  strokeWidth="6"
                  strokeLinecap="round"
                />
                <polygon points="8,28 0,14 16,14" fill="white" />
                <line
                  x1="8"
                  y1="1"
                  x2="8"
                  y2="17"
                  stroke="var(--blue-primary)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <polygon points="8,27 2,15 14,15" fill="var(--blue-primary)" />
              </svg>
            ))}
          </div>
          {caption && (
            <figcaption
              ref={captionRef}
              className="ct__figure-caption"
              dangerouslySetInnerHTML={{ __html: processText(caption) }}
            />
          )}
        </figure>
      </div>
    </div>
  );
}

function RefIntFigure({ item }) {
  const figNumber = extractTagNumber(item.data?.text);
  return (
    <div className="sp__ref-section">
      {figNumber && (
        <h2 className="sp__title sp__title--section">Figure {figNumber}</h2>
      )}
      {item.title && <p className="sp__ref-subtitle">{item.title}</p>}
      <div className="sp__intfig-wrap">
        <IntFigureComp item={item} showExpand={false} />
      </div>
    </div>
  );
}

function RefIntFigureDiscrete({ item }) {
  const figNumber = extractTagNumber(item.data?.text);
  return (
    <div className="sp__ref-section">
      {figNumber && (
        <h2 className="sp__title sp__title--section">Figure {figNumber}</h2>
      )}
      {item.title && <p className="sp__ref-subtitle">{item.title}</p>}
      <div className="sp__intfig-wrap">
        <IntFigureDiscreteComp item={item} showExpand={false} />
      </div>
    </div>
  );
}

/** Multi-figure in the support panel — uses the shared manual viewer. */
function RefMultiFigure({ item }) {
  const { text, image, multitype } = item.data ?? {};
  const allImages = (image ?? "").split(";;;split;;;");
  const allCaptions = (text ?? "").split(";;;split;;;");
  const figNumber = extractTagNumber(allCaptions[0]);

  const isBackground = multitype === "background-manual";
  const images = isBackground ? allImages.slice(1) : allImages;
  const captions = isBackground ? allCaptions.slice(1) : allCaptions;
  const bgImage = isBackground ? allImages[0] : undefined;

  return (
    <div className="sp__ref-section">
      {figNumber && (
        <h2 className="sp__title sp__title--section">Figure {figNumber}</h2>
      )}
      {item.title && <p className="sp__ref-subtitle">{item.title}</p>}
      <MultiFigureViewer
        images={images}
        captions={captions}
        backgroundImage={bgImage}
      />
    </div>
  );
}

/* ─── Regular support article content dispatcher ─────────────── */

function SupportItem({ item }) {
  switch (item.type) {
    case "SUPPORTCOMP_TITLE":
      return <SupportTitle item={item} />;
    case "SUPPORTCOMP_PARAGRAPH":
      return <SupportParagraph item={item} />;
    case "SUPPORTCOMP_EQUATION":
    case "SUPPORTCOMP_EQN_MULTILINE":
      return <SupportEquation item={item} />;
    case "SUPPORTCOMP_IMAGE":
      return <SupportImage item={item} />;
    default:
      return null;
  }
}

function SupportTitle({ item }) {
  const Tag = item.data?.level === "subsection" ? "h3" : "h2";
  return (
    <Tag className={`sp__title sp__title--${item.data?.level ?? "section"}`}>
      {item.title}
    </Tag>
  );
}

function SupportParagraph({ item }) {
  const { text, paragraph_title } = item.data ?? {};
  const html = paragraph_title
    ? `<span class="sp__para-title">${paragraph_title}</span> ${processText(text ?? "")}`
    : processText(text ?? "");
  return (
    <div className="sp__para">
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

function SupportEquation({ item }) {
  const { equation } = item.data ?? {};
  return (
    <div className="sp__equation">
      {/* {item.title && <span className="sp__eq-label">{item.title}</span>} */}
      <div className="sp__eq-content">{ensureDisplayMath(equation)}</div>
    </div>
  );
}

function SupportImage({ item }) {
  const { image } = item.data ?? {};
  const src = image
    ? image.startsWith("data:")
      ? image
      : svgToDataUrl(image)
    : null;
  return (
    <figure className="sp__figure">
      {src && (
        <img
          src={src}
          alt={item.title ?? "Figure"}
          className="sp__figure-img"
        />
      )}
      {/* {item.title && <figcaption className="sp__figure-caption">{item.title}</figcaption>} */}
    </figure>
  );
}

/* ─── Icon components ────────────────────────────────────────── */

function BackIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6.5 2L3.5 5l3 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ForwardIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.5 2l3 3-3 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
